// Apis not required user login
import { v4 as uuidv4 } from 'uuid'
import {
    get,
    getDatabase,
    ref,
    set as dbSet,
    query,
    equalTo,
    orderByChild,
    update,
    runTransaction,
    set,
    push,
    serverTimestamp,
} from 'firebase/database'
import {
    getAuth,
    signInWithEmailAndPassword,
    updateEmail,
    updatePassword,
    signOut,
} from 'firebase/auth'
import {
    FirebaseBlogData,
    FirebaseGuidesData,
    Product,
    Products,
    ProfessionalLearningResource,
    ShopTerms,
    Terms,
    UpcomingTerms,
    Res,
    getEmploymentDeclaration,
    EmploymentDeclarationSubmission,
    EmploymentDeclaration,
    HowDidYouHearType,
    CandidateRefereeSurveyResponse,
    CandidateRefereeSurveyRequest,
    UserTerms,
    TermsDocuments,
    SuperFunds,
} from './types'
import { app, auth, db } from './configs'
import {
    ref as stRef,
    getStorage,
    deleteObject,
    uploadBytesResumable,
    TaskState,
    getDownloadURL,
} from 'firebase/storage'
import { getUserStoragePath } from './helpers'
import { setUserRegDefault, UserUpdate } from '../redux/userRegistrationSlice'
import { getIP } from '../helpers/getIp'
import { AccountType, TeacherEmploymentSetting, UserType } from '../redux/types'
import { Dispatch } from 'redux'
import { setUserLogout } from '../redux/userSlice'
import { setSchoolDefault } from '../redux/schoolSlice'
import { FieldState, TeacherAccountState } from './types-teacher'
import { setCentreDefault } from '../redux/eccSlice'
import { updateUserDetails } from './users-api'
import { FirebaseError } from 'firebase/app'
import { resetSchoolBookings } from '../redux/schoolBookingsSlice'
import { QueryClient } from '@tanstack/react-query'
import { LocationParams } from './school-api'
import type { HelpDeskEnquiry } from '../pages/Dashboard/HelpDeskPage'
import dayjs from '../helpers/dayjs'
import { logger } from '../helpers/logger'
import { getError } from '../helpers/utils'
import { ReferenceResponse_v3 } from '../pages/RefereeSurveyPage/components/ReferenceSurvey_v3'

interface IStoreHelpDeskEnquiry {
    userId: string | null
    data: HelpDeskEnquiry
}

export const storeHelpDeskEnquiry = ({
    userId,
    data,
}: IStoreHelpDeskEnquiry) => {
    const timeStamp = dayjs().unix()
    const db = getDatabase()

    return push(ref(db, `helpDesk`), {
        userId: userId || null,
        timeStamp,
        ...data,
    })
}

export const signInUserWithEmailAndPassword = async (
    email: string,
    password: string
): Promise<
    | { success: true; uid: string; emailIsVerified: boolean }
    | { success: false; errorCode: string }
> => {
    try {
        const auth = getAuth(app)
        const userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
        )
        const user = userCredential.user

        return {
            success: true,
            uid: user.uid,
            emailIsVerified: user.emailVerified,
        }
    } catch (error) {
        if (error instanceof FirebaseError) {
            return { success: false, errorCode: error.code }
        } else {
            return { success: false, errorCode: 'unknown' }
        }
    }
}

export const signOutUser = async (
    dispatch: Dispatch,
    queryClient: QueryClient,
    successCallback?: () => void,
    errorCallback?: () => void
) => {
    try {
        await signOut(getAuth())
        successCallback && successCallback()
        sessionStorage.removeItem('spark-auth-token')
        dispatch(setUserLogout())
        dispatch(setUserRegDefault())
        dispatch(setSchoolDefault())
        dispatch(setCentreDefault())
        dispatch(resetSchoolBookings())

        // Clear react query
        queryClient.clear()
    } catch (e) {
        logger.error(e)
        errorCallback && errorCallback()
    }
}

export const getTermsData = async () => {
    const db = getDatabase()
    const reference = ref(db, `termsV2`)

    return await get(reference).then((snapShot) => {
        return snapShot.toJSON() as Terms
    })
}

export const getTermsDataNew = async () => {
    const db = getDatabase()
    const reference = ref(db, `terms`)
    return await get(reference).then((snapShot) => {
        return snapShot.toJSON() as UserTerms
    })
}

export const getTermsByAccountType = async (
    country: string,
    state: string,
    type: 'childcareCentres' | 'schools' | 'educators'
): Promise<TermsDocuments | null> => {
    const db = getDatabase()
    const reference = ref(
        db,
        `countries/${country}/${state}/termsAndConditions/${type}/documents`
    )
    return (await get(reference)).val()
}

export const getUpcomingTerms = async () => {
    const db = getDatabase()
    const reference = ref(db, `upcomingTermsV2`)

    return await get(reference).then((snapShot) => {
        return (snapShot.toJSON() ?? {}) as UpcomingTerms
    })
}

export const getAllBlogs = async () => {
    const db = getDatabase()
    const reference = ref(db, `blog/published/`)
    const myQuery = query(reference, orderByChild('visibility'), equalTo(true))

    return await get(myQuery).then((snapShot) => {
        return snapShot.toJSON() as { [index: string]: FirebaseBlogData } | null
    })
}

export const getBlogByBlogId = async (id: string) => {
    const db = getDatabase()
    const reference = ref(db, `blog/published/${id}`)

    return await get(reference).then((snapShot) => {
        return snapShot.toJSON() as FirebaseBlogData | null
    })
}

export const getGuidesData = async (isTeacher: boolean) => {
    const db = getDatabase()
    const reference = ref(
        db,
        `publicDocuments/${isTeacher ? 'teacherGuides' : 'schoolGuides'}`
    )
    const myQuery = query(reference, orderByChild('visibility'), equalTo(true))

    return await get(myQuery).then((snapShot) => {
        // logger.debug(snapShot.toJSON())
        return snapShot.toJSON() as FirebaseGuidesData | null
    })
}

export const getProfessionalLearningResources = async () => {
    const db = getDatabase()
    const reference = ref(db, `resources`)

    try {
        const dataSnapshot = await get(reference)
        const professionalLearningResources = await dataSnapshot.toJSON()

        return (professionalLearningResources ?? {}) as {
            [resource: string]: ProfessionalLearningResource
        }
    } catch (error) {
        logger.error(
            'Error happens when trying to fetch professional learning resources of teachers.'
        )
        logger.error(error)
    }
}
// Updated to fetch resources from each respective state
export const getProfessionalLearningResourcesV2 = async (
    country: string,
    state: string
) => {
    const db = getDatabase()
    const reference = ref(db, `countries/${country}/${state}/resources`)

    try {
        const dataSnapshot = await get(reference)
        const professionalLearningResources = dataSnapshot.toJSON()

        return (professionalLearningResources ?? {}) as {
            [resource: string]: ProfessionalLearningResource
        }
    } catch (error) {
        logger.error(
            'Error happens when trying to fetch professional learning resources of teachers.'
        )
        logger.error(error)
    }
}

export const getAllMerches = async () => {
    const db = getDatabase()
    const reference = ref(db, `/shop/products`)

    try {
        const dataSnapshot = await get(reference)
        const merches = await dataSnapshot.toJSON()

        return (merches ?? {}) as Products
    } catch (error) {
        logger.error('Errors happen when trying to get all merches.')
        logger.error(error)
    }
}

export const getMerchById = async (id: string) => {
    const db = getDatabase()
    const reference = ref(db, `/shop/products/${id}`)

    try {
        const dataSnapshot = await get(reference)
        const merchandise = await dataSnapshot.toJSON()

        return merchandise as Product
    } catch (error) {
        logger.error(
            `Errors happen when trying to get merchandise with id ${id}.`
        )
        logger.error(error)
    }
}

export const getStock = async (
    fbId: string,
    color: string,
    size: string
): Promise<
    Res<{
        color: string
        size: string
        fbId: string
        stock: number
        stripeId: string
    }>
> => {
    try {
        const db = getDatabase()
        const reference = ref(
            db,
            `/shop/products/${fbId}/options/${color}/sizes/${size}`
        )
        const dataSnapshot = await get(reference)
        const sizeData = (await dataSnapshot.toJSON()) as {
            stripeId: string
            stock: number
        }

        return {
            code: 200,
            data: {
                stripeId: sizeData.stripeId,
                color,
                size,
                fbId,
                stock: (sizeData.stock ?? 0) as number,
            },
        }
    } catch (error) {
        logger.error(
            `Fail to get the stock of product productId: ${fbId}, color: ${color}, size: ${size}`
        )
        logger.error(error)
        return { code: 500 }
    }
}

export const getShopTerms = async () => {
    try {
        const db = getDatabase()
        const reference = ref(db, `/shop/terms`)
        const dataSnapshot = await get(reference)
        const shopTerms = (await dataSnapshot.toJSON()) as ShopTerms | null
        if (!shopTerms) throw new Error('Shop terms data is empty')

        return { code: 200, value: shopTerms }
    } catch (error) {
        logger.error('Errors happen when trying to load shop terms.')
        logger.error(error)
        return { code: 500 }
    }
}

/* Teacher Application (Employment Declarations) APIs */

export const retrieveEmployentDeclaration = async (
    teacherId: string
): Promise<Res<EmploymentDeclaration>> => {
    try {
        const dbRef = ref(getDatabase(), `employmentDeclarations/${teacherId}`)
        const snap = await get(dbRef)
        if (snap.exists()) {
            if (snap.val().dateResponded) return { code: 500 }
            return { data: getEmploymentDeclaration(snap), code: 200 }
        } else {
            return { code: 500 }
        }
    } catch (error) {
        logger.error(
            'Error happened whilst trying to retrieve teacher information'
        )
        logger.error(error)
        return { code: 500 }
    }
}

export const submitEmploymentDeclaration = async (
    teacherId: string,
    data: EmploymentDeclarationSubmission
) => {
    try {
        await update(
            ref(getDatabase(), `employmentDeclarations/${teacherId}`),
            data
        )
        return { code: 200 }
    } catch (error) {
        logger.debug(`Failed to submit dec check: ${getError(error)}`)

        return { code: 500, error }
    }
}

// User Profile functions

export const removeProfilePicture = async (userId: string) => {
    try {
        const storageRef = stRef(getStorage(), getUserStoragePath(userId))
        deleteObject(storageRef)

        const userRef = ref(getDatabase(), `users/${userId}`)
        await update(userRef, {
            profilePictureURL: null,
        })

        const teacherRef = ref(
            getDatabase(),
            `teachers/${userId}/profilePicture`
        )
        await update(teacherRef, {
            value: null,
        })
    } catch (error) {
        logger.error(
            'Error happened whilst trying to remove your profile picture.'
        )
        logger.error(error)
        return { code: 500 }
    }
}

export const updateUserPhoto = async (uid: string, photoUrl: string) => {
    try {
        const docRef = ref(getDatabase(), `users/${uid}`)
        await update(docRef, {
            profilePictureURL: photoUrl,
        })
        const teacherRef = ref(getDatabase(), `teachers/${uid}/profilePicture`)
        await update(teacherRef, {
            value: photoUrl,
        })
        return { code: 200, data: 'Update photo successfully.' }
    } catch (error) {
        return { code: 500 }
    }
}

// const metadatas = {
//   pdf: { contentType: 'application/pdf' },
//   xls: { contentType: 'application/vnd.ms-excel' },
//   xlsx: { contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
//   doc: { contentType: 'application/msword' },
//   docx: { contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
//   jpg: { contentType: 'image/jpeg' },
//   jpeg: { contentType: 'image/jpeg' },
//   png: { contentType: 'image/png' },
//   csv: { contentType: 'text/csv' }, // Add appropriate MIME type for CSV
// };

interface IuploadFileToStorageWithProgress {
    path: string
    file: Blob
    onProgress: (taskState: TaskState, progress: number) => void
}

// export const uploadFileToStorageWithProgress = async ({ path, file, onProgress }: IuploadFileToStorageWithProgress): Promise<Res<string[]>> => {
//   const storage = getStorage();
//   return new Promise((resolve, reject) => {
//     const storageRef = stRef(storage, path);
//     const uploadTask = uploadBytesResumable(storageRef, file);
//     uploadTask.on("state_changed",
//       (snapshot) => {
//         const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
//         onProgress(snapshot.state, progress);
//       },
//       (error) => {
//         console.error(error.message);
//         switch (error.code) {
//           case "storage/unauthorized": reject({ error: 500, errorMsg: "User doesn't have permission to access the object when uploading general file", }); break;
//           case "storage/canceled": reject({ error: 500, errorMsg: "User canceled the upload when uploading general file", }); break;
//           case "storage/unknown": reject({ error: 500, errorMsg: "Unknown error occurred when uploading general file", }); break;
//           default: reject({ error: 500, errorMsg: "Unknown error occurred" + error.message, }); break;
//         };
//       },
//       () => { getDownloadURL(uploadTask.snapshot.ref).then((fileDownloadUrl) => { resolve({ code: 200, data: [uploadTask.snapshot.ref.name, fileDownloadUrl] }); }); }
//     );
//   });
// };

export class FileUploadError extends Error {
    code: number

    constructor(code: number, message: string) {
        super(message)
        this.code = code
    }
}

export const uploadFileToStorageWithProgress = async ({
    path,
    file,
    onProgress,
    maxRetries = 2,
    timeoutMs = 30000, // timeout for detecting blocked requests
}: IuploadFileToStorageWithProgress & {
    maxRetries?: number
    timeoutMs?: number
}): Promise<Res<string[]>> => {
    const storage = getStorage()

    const attemptUpload = (): Promise<Res<string[]>> => {
        return new Promise((resolve, reject) => {
            const storageRef = stRef(storage, path)
            const uploadTask = uploadBytesResumable(storageRef, file)

            let hasSettled = false
            const timeout = setTimeout(() => {
                if (!hasSettled) {
                    uploadTask.cancel() // force stop
                    reject(
                        new FileUploadError(
                            408,
                            `Upload attempt timed out. Possibly blocked network - please connect to a different network and try again.`
                        )
                    )
                }
            }, timeoutMs)

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress =
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    onProgress(snapshot.state, progress)
                },
                (error) => {
                    clearTimeout(timeout)
                    hasSettled = true
                    logger.error(error.message)

                    switch (error.code) {
                        case 'storage/unauthorized':
                            reject(
                                new FileUploadError(
                                    403,
                                    `Upload attempt timed out. Possibly blocked network - please connect to a different network and try again.`
                                )
                            )
                            break
                        case 'storage/canceled':
                            reject(
                                new FileUploadError(
                                    499,
                                    'Upload canceled by user.'
                                )
                            )
                            break
                        case 'storage/unknown':
                            reject(
                                new FileUploadError(
                                    520,
                                    'Unknown error occurred.'
                                )
                            )
                            break
                        case 'storage/retry-limit-exceeded':
                        case 'storage/network-request-failed':
                            reject(
                                new FileUploadError(
                                    503,
                                    'Network error or blocked upload.'
                                )
                            )
                            break
                        default:
                            reject(
                                new FileUploadError(
                                    500,
                                    'Unexpected error :' + error.message
                                )
                            )
                    }
                },
                () => {
                    clearTimeout(timeout)
                    hasSettled = true
                    logger.debug(
                        `Getting file from: ${uploadTask.snapshot.ref.fullPath}`
                    )
                    getDownloadURL(uploadTask.snapshot.ref)
                        .then((fileDownloadUrl) => {
                            resolve({
                                code: 200,
                                data: [
                                    uploadTask.snapshot.ref.name,
                                    fileDownloadUrl,
                                ],
                            })
                        })
                        .catch((e) => {
                            resolve({
                                code: 500,
                                error: {
                                    msg: 'Unauthorised access',
                                },
                            })
                        })
                }
            )
        })
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await attemptUpload()
        } catch (err) {
            if (attempt === maxRetries) {
                throw err // after final attempt, bubble it up
            } else {
                logger.warn(
                    `Retrying upload (attempt ${attempt + 1}/${maxRetries})...`
                )
            }
        }
    }

    throw new Error('Unknown failure.')
}

export const isObjectEmpty = (obj?: Object | null): boolean => {
    return !obj ? true : Object.keys(obj).length === 0
}

export const updateUserInfo = async (
    uid: string,
    updateData: UserUpdate,
    set?: boolean
) => {
    try {
        let { user, teacher } = updateData
        if (user && user.data && !isObjectEmpty(user.data)) {
            const docRef = ref(
                getDatabase(),
                `users/${uid}${user.path ? user.path : ''}`
            )
            await update(docRef, user.data)
        }
        if (teacher && teacher.data && !isObjectEmpty(teacher.data)) {
            const teacherRef = ref(
                getDatabase(),
                `teachers/${uid}${teacher.path ? teacher.path : ''}`
            )
            if (set) {
                await dbSet(teacherRef, teacher.data)
            } else {
                await update(teacherRef, teacher.data)
            }

            const updateRef = ref(getDatabase(), `teachers/${uid}/accountState`)
            await update(updateRef, {
                firstTime: false,
            })
        }
        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const updateUserEmail = async (
    email: string,
    password: string,
    newEmail: string,
    emailOnly?: boolean
) => {
    try {
        if (!auth.currentUser) {
            throw new Error('Not authenticated!')
        }
        const res = await signInUserWithEmailAndPassword(email, password)
        if (!res.success) {
            return { code: 500, errorMsg: res.errorCode }
        }
        await updateEmail(auth.currentUser, newEmail)
        await updateUserDetails(auth.currentUser.uid, { email: newEmail })
        return { code: 200 }
    } catch (error: any) {
        logger.error('There was an error updating user email')

        return { code: 500, errorMsg: error.code }
    }
}

export const updateUserPassword = async (
    email: string,
    password: string,
    newPassword: string
) => {
    try {
        if (!auth.currentUser) {
            throw new Error('Not authenticated!')
        }
        const res = await signInUserWithEmailAndPassword(email, password)
        if (!res.success) {
            return { code: 500, errorMsg: res.errorCode }
        }
        await updatePassword(auth.currentUser, newPassword)
        return { code: 200 }
    } catch (error: any) {
        logger.error('There was an error updating user password')
        return { code: 500, errorMsg: error.code }
    }
}

export const removeStorageFile = async (path: string) => {
    const storage = getStorage()
    const objRef = stRef(storage, path)
    await deleteObject(objRef)
}

export const submitHowDidYouHear = async (
    userId: string,
    hdyh: HowDidYouHearType
) => {
    try {
        const temp: HowDidYouHearType = {
            ...hdyh,
        }
        const userNode = ref(getDatabase(), `teachers/${userId}/howDidYouHear`)

        const data = await get(userNode)
        if (!data.val() || !data.val().firstRequest) {
            temp.firstRequest = temp.lastRequest
        }

        logger.debug('Sending Request...')
        logger.debug(temp, userId)

        await update(userNode, temp)
        return { code: 200 }
    } catch (error) {
        return { code: 500, error: error }
    }
}

export const submitRefereeSurvey = async (
    refereeId: string,
    survey: CandidateRefereeSurveyResponse
) => {
    try {
        const refereeNode = ref(
            getDatabase(),
            `referenceReview/${refereeId}/response`
        )

        let ip
        try {
            ip = await getIP()
        } catch (e) {
            ip = ''
        }

        await update(refereeNode, {
            ...survey,
            ip,
            timeResponded: serverTimestamp(),
        })
        return { code: 200 }
    } catch (error: any) {
        logger.error(`${error.message}`)
        return { code: 500, error: error }
    }

    // try {
    //   // Delete reference request node after submission
    //   const referenceNode = ref(getDatabase(), `referenceReview/${refereeId}`);
    //   await remove(referenceNode);

    //   return {code: 200}
    // } catch (error) {
    //   return {code: 500, error: error}
    // }
}

export const checkRefereeValidity = async (
    refereeId: string
): Promise<Res<CandidateRefereeSurveyRequest | undefined>> => {
    try {
        const refReviewNode = ref(getDatabase(), `referenceReview/${refereeId}`)
        const snap = await get(refReviewNode)
        if (snap.exists()) {
            return {
                code: 200,
                data: snap.val() as CandidateRefereeSurveyRequest,
            }
        }

        return { code: 200, data: undefined }
    } catch (error) {
        logger.error(error)
        return { code: 500, error: error }
    }
}

export const fetchDocLink = async (
    docSlug: string
): Promise<Res<string | null>> => {
    try {
        const docRef = ref(getDatabase(), `links/${docSlug}`)
        const snap = await get(docRef)
        if (snap.exists()) {
            return { code: 200, data: snap.val().url }
        } else {
            return { code: 200, data: null }
        }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}
export const fetchPayslipLink = async (
    uid: string,
    fYear: string,
    pId: string
): Promise<Res<string | null>> => {
    try {
        const docRef = ref(getDatabase(), `payslips/${uid}/${fYear}/${pId}/url`)
        const snap = await get(docRef)
        if (snap.exists()) {
            return { code: 200, data: snap.val() }
        } else {
            return { code: 200, data: null }
        }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const selectUserAccountType = async (
    userId: string,
    // form: NewAccountSubmission,
    data:
        | {
              type: Extract<AccountType, 'teacher'>
              locationState: string
              employmentSetting: TeacherEmploymentSetting
          }
        | {
              type: Exclude<AccountType, 'teacher'>
          },
    user?: UserType,
    existingUser?: true
) => {
    try {
        let userRef = ref(getDatabase(), `users/${userId}`)
        await update(userRef, {
            accountType: data.type,
        })

        if (data.type === 'teacher') {
            let teacherRef = ref(getDatabase(), `teachers/${userId}`)
            await update(teacherRef, {
                accountState: {
                    value: TeacherAccountState.INCOMPLETE,
                    firstTime: !existingUser,
                    // firstTime: !existingUser,
                },
                employmentSetting: {
                    [`${uuidv4()}`]: {
                        locationState: data.locationState,
                        type: data.employmentSetting,
                    },
                },
                ...(user
                    ? {
                          ...(user.firstName
                              ? {
                                    firstName: {
                                        value: user.firstName,
                                        state: FieldState.SUBMITTED,
                                    },
                                }
                              : null),
                          ...(user.firstNameActual
                              ? {
                                    firstName: {
                                        value: user.firstNameActual,
                                        state: FieldState.SUBMITTED,
                                    },
                                }
                              : null),
                          ...(user.lastName
                              ? {
                                    lastName: {
                                        value: user.lastName,
                                        state: FieldState.SUBMITTED,
                                    },
                                }
                              : null),
                          ...(user.email
                              ? {
                                    email: {
                                        value: user.email,
                                        state: FieldState.SUBMITTED,
                                    },
                                }
                              : null),
                      }
                    : null),
            })
        }

        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const updateEmploymentSetting = async (
    userId: string,
    locationState: string,
    workType: TeacherEmploymentSetting,
    accountState?: TeacherAccountState
) => {
    try {
        const teacherRef = ref(
            getDatabase(),
            `teachers/${userId}/employmentSetting`
        )
        await set(teacherRef, {
            [`${uuidv4()}`]: {
                locationState: locationState,
                type: workType,
                state: accountState || FieldState.INCOMPLETE,
            },
        })

        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}
export const addEmploymentSetting = async (
    userId: string,
    locationState: string,
    workType: TeacherEmploymentSetting
) => {
    try {
        const teacherRef = ref(
            getDatabase(),
            `teachers/${userId}/employmentSetting`
        )
        await update(teacherRef, {
            [`${uuidv4()}`]: {
                locationState: locationState,
                type: workType,
                state: TeacherAccountState.INCOMPLETE,
            },
        })

        return { code: 200 }
    } catch (error: any) {
        return { code: 500, error: error }
    }
}

export const fetchECCRates = async () => {
    return (
        await get(
            ref(getDatabase(), `countries/Australia/VIC/childcareCentres/rates`)
        )
    ).val()
}

export const GENERAL_API = {
    fetchSuperFunds: async (): Promise<SuperFunds | null> => {
        return (await get(ref(db, 'superFunds'))).val()
    },
    fetchCalendarDates: async (args: LocationParams): Promise<any> => {
        logger.debug('fetch calendar')
        return (
            await get(
                ref(
                    db,
                    `countries/${args.country}/${args.locationState}/calendar`
                )
            )
        ).val()
    },
    fetchSupportStaffLanguages: async (): Promise<Record<
        string,
        string
    > | null> =>
        (await get(ref(db, `publicResources/supportStaffLanguages`))).val(),

    updateUserTimestamp: async (userId: string): Promise<Res<void>> => {
        try {
            const userRef = ref(getDatabase(), `users/${userId}`)
            const transaction = await runTransaction(userRef, (user) => {
                if (user) {
                    user.lastVerified = dayjs().unix()
                }
                return user
            })

            if (!transaction.committed) {
                return {
                    code: 500,
                    error: new Error(
                        `User node does not exist or is not an object for userId: ${userId}`
                    ),
                }
            }

            return { code: 200, data: undefined }
        } catch (error: any) {
            logger.error(error)
            return { code: 500, error: error }
        }
    },

    fetchEmploymentReview: async (teacherId: string) =>
        get(ref(db, `employmentDeclarations/${teacherId}`)),

    referenceSurvey: {
        get: async (refereeId: string) =>
            get(ref(db, `referenceReview/${refereeId}`)),
        updateVisitedTimeStamp: async (refereeId: string) =>
            push(ref(db, `referenceReview/${refereeId}/visited`), {
                timeStamp: dayjs().unix(),
                appVersion: process.env.REACT_APP_VERSION || 'unknown',
            }),
        submit_v3: async (
            refereeId: string,
            response: ReferenceResponse_v3
        ) => {
            let ip: string | null
            try {
                ip = await getIP()
            } catch (e) {
                logger.warn(`Failed to get IP`)
                ip = null
            }

            const responseRef = ref(db, `referenceReview/${refereeId}/response`)

            await set(responseRef, {
                version: 3,
                state: 'submitted',
                ip,
                timeResponded: serverTimestamp(),
                data: response,
            })
            return Promise.resolve()
        },
        denySubmission: async (refereeId: string) => {
            let ip: string | null
            try {
                ip = await getIP()
            } catch (e) {
                logger.warn(`Failed to get IP`)
                ip = null
            }
            await set(ref(db, `referenceReview/${refereeId}/response`), {
                ip,
                timeResponded: serverTimestamp(),
                state: 'declined',
            })
            return Promise.resolve()
        },
    },
} as const
